/** Notification ding — plays a short mp3 clip. Works on all browsers including mobile Safari. */

// Preload the audio file on import
const DING_URL = "/ding.mp3";
let _preloaded = false;

function preload() {
  if (_preloaded) return;
  _preloaded = true;
  // Fetch into browser cache so playback is instant
  fetch(DING_URL).catch(() => {});
}

// Preload on first user interaction (required for mobile)
if (typeof document !== "undefined") {
  const touch = () => {
    preload();
    document.removeEventListener("click", touch);
    document.removeEventListener("touchstart", touch);
  };
  document.addEventListener("click", touch, { once: true });
  document.addEventListener("touchstart", touch, { once: true });
  // Also preload immediately in case we're on desktop
  preload();
}

export function playDing() {
  // Create a new Audio each time — avoids issues with reuse, loading state, and concurrent plays
  const audio = new Audio(DING_URL);
  audio.volume = 1.0;
  audio.play().catch((err) => {
    console.warn("[ding] play blocked:", err.message);
  });
}

export type TimerSiren = "none" | "single" | "liveheats";
export type TimerCue = "start" | "warning" | "end";
let hornContext: AudioContext | null = null;
let mutedFallback = false;

/** Call from a Producer/Run/TV user gesture to unlock sound on mobile Safari. */
export async function unlockTimerAudio(): Promise<void> {
  if (typeof AudioContext === "undefined") return;
  hornContext ??= new AudioContext();
  await hornContext.resume();
}

/** Device-local preference; Judge pads never call this function. */
export function setTimerMuted(muted: boolean): void {
  mutedFallback = muted;
  try {
    window.localStorage.setItem("scorio.timer.muted", String(muted));
  } catch {
    /* Private browsing may block storage. */
  }
}

export function timerMuted(): boolean {
  try {
    return window.localStorage.getItem("scorio.timer.muted") === "true";
  } catch {
    return mutedFallback;
  }
}

/** Horn cues are synthesized locally and do not download third-party audio. */
export function playTimerCue(cue: TimerCue, siren: TimerSiren): void {
  if (siren === "none" || timerMuted() || !hornContext || hornContext.state !== "running") return;
  if (cue === "warning" && siren !== "liveheats") return;
  const count = cue === "end" && siren === "liveheats" ? 2 : cue === "warning" ? 3 : 1;
  const now = hornContext.currentTime;
  for (let index = 0; index < count; index++) {
    const start = now + index * 0.55;
    const oscillator = hornContext.createOscillator();
    const gain = hornContext.createGain();
    oscillator.type = cue === "warning" ? "sine" : "sawtooth";
    oscillator.frequency.value = cue === "warning" ? [523.25, 659.25, 783.99][index] : 220;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.15, start + 0.025);
    gain.gain.setValueAtTime(0.15, start + 0.3);
    gain.gain.linearRampToValueAtTime(0, start + 0.4);
    oscillator.connect(gain);
    gain.connect(hornContext.destination);
    oscillator.start(start);
    oscillator.stop(start + 0.42);
  }
}
